import { useState } from 'react';
import { Button } from 'src/components';
import styles from './styles.module.scss';

const MCP_URL = 'https://mcp.filespin.io/mcp';

type MCPConnectGuideProps = {
  onConnected: () => void;
  /** Header copy. Defaults to the standard onboarding copy. */
  title?: string;
  description?: string;
  /** Hide the "I've connected" CTA (when embedded as a secondary expander). */
  hideCta?: boolean;
};

const MCPConnectGuide = ({
  onConnected,
  title = 'Connect an AI agent',
  description = 'Follow these steps to connect Claude, ChatGPT, Mistral or any other MCP-compatible AI client to FileSpin.',
  hideCta = false,
}: MCPConnectGuideProps) => {
  const [expandedStep, setExpandedStep] = useState<number>(1);
  const [showGenericConfig, setShowGenericConfig] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(label);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  return (
    <>
      <div className={styles.guideHeader}>
        <h2>{title}</h2>
        <p>{description}</p>
      </div>

      <div className={styles.guideSteps}>
        <div className={styles.guideStep}>
          <div
            className={styles.guideStepHeader}
            onClick={() => setExpandedStep(expandedStep === 1 ? 0 : 1)}
          >
            <div className={styles.stepNumber}>1</div>
            <h4 className={styles.stepTitle}>Open your AI Application MCP Settings</h4>
            <span className={`${styles.stepChevron} ${expandedStep === 1 ? styles.stepChevronOpen : ''}`}>
              ▾
            </span>
          </div>
          {expandedStep === 1 && (
            <div className={styles.stepBody}>
              <ul className={styles.clientList}>
                <li className={styles.clientItem}>
                  <span>
                    <span className={styles.clientName}>Claude</span> — Settings/Customize &gt; Connectors &gt; Add Connector
                    <span className={styles.clientSubtext}>For Claude Org accounts, the Org admin must add the FileSpin MCP connector to the organization first before it can be used.</span>
                  </span>
                </li>
                <li className={styles.clientItem}>
                  <span><span className={styles.clientName}>ChatGPT</span> — Settings &gt; Apps &gt; Add Custom App</span>
                </li>
                <li className={styles.clientItem}>
                  <span><span className={styles.clientName}>Mistral</span> — Intelligence &gt; Connectors &gt; Add Custom Connector</span>
                </li>
              </ul>
            </div>
          )}
        </div>

        <div className={styles.guideStep}>
          <div
            className={styles.guideStepHeader}
            onClick={() => setExpandedStep(expandedStep === 2 ? 0 : 2)}
          >
            <div className={styles.stepNumber}>2</div>
            <h4 className={styles.stepTitle}>Add FileSpin MCP Server</h4>
            <span className={`${styles.stepChevron} ${expandedStep === 2 ? styles.stepChevronOpen : ''}`}>
              ▾
            </span>
          </div>
          {expandedStep === 2 && (
            <div className={styles.stepBody}>
              Add FileSpin MCP Server with a suitable name such as <strong>FileSpin MCP</strong>.
              <div className={styles.codeBlock}>
                <button
                  type="button"
                  className={styles.copyButton}
                  onClick={() => handleCopy(MCP_URL, 'url')}
                >
                  {copied === 'url' ? 'Copied!' : 'Copy'}
                </button>
                {MCP_URL}
              </div>
              Click <strong>Save</strong> and then <strong>Connect</strong>.
            </div>
          )}
        </div>

        <div className={styles.guideStep}>
          <div
            className={styles.guideStepHeader}
            onClick={() => setExpandedStep(expandedStep === 3 ? 0 : 3)}
          >
            <div className={styles.stepNumber}>3</div>
            <h4 className={styles.stepTitle}>Authorise via OAuth</h4>
            <span className={`${styles.stepChevron} ${expandedStep === 3 ? styles.stepChevronOpen : ''}`}>
              ▾
            </span>
          </div>
          {expandedStep === 3 && (
            <div className={styles.stepBody}>
              When you connect, a browser window will open for you to log into FileSpin to authorise the connection.
              After authorising, control will return to the AI client and the connection is established automatically.
            </div>
          )}
        </div>

        <div className={styles.guideStep}>
          <div
            className={styles.guideStepHeader}
            onClick={() => setExpandedStep(expandedStep === 4 ? 0 : 4)}
          >
            <div className={styles.stepNumber}>4</div>
            <h4 className={styles.stepTitle}>Approve Tool Access</h4>
            <span className={`${styles.stepChevron} ${expandedStep === 4 ? styles.stepChevronOpen : ''}`}>
              ▾
            </span>
          </div>
          {expandedStep === 4 && (
            <div className={styles.stepBody}>
              When you first ask the AI to interact with FileSpin, your client will prompt you to approve the MCP tools. We recommend allowing all tools for the best experience.
              <br /><br />
              <em>FileSpin MCP does not expose asset deletion — this is by design to prevent accidental loss of assets.</em>
            </div>
          )}
        </div>
      </div>

      <button
        type="button"
        className={styles.toggleLink}
        onClick={() => setShowGenericConfig(!showGenericConfig)}
      >
        {showGenericConfig ? 'Hide generic config' : 'Using another AI client?'}
      </button>

      {showGenericConfig && (
        <div className={styles.infoBox} style={{ marginTop: '0.75rem' }}>
          <span>
            Any MCP-compatible AI client can connect to FileSpin using the URL <code>https://mcp.filespin.io/mcp</code>.
            The server uses OAuth for authentication. Refer to your AI client's documentation for how to add an MCP server.
          </span>
        </div>
      )}

      {!hideCta && (
        <div className={styles.guideCta} style={{ marginTop: '1.5rem' }}>
          <Button onClick={onConnected}>I've connected</Button>
        </div>
      )}
    </>
  );
};

export default MCPConnectGuide;
